/**
 * A new agent from a description (V1-E09-T04): the copilot drafts it around a template's or an agent's providers;
 * the draft is shown (what it will say and do, the checks beside it) and created only when the user says so.
 * V1.2-UX-T04: whether it answers or places calls, what it's for, and its language (guessed from the description's
 * script); the server picks speech for that language unless providers are chosen under "More".
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Sparkles } from "lucide-react";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { ApiError, data, type Schemas } from "../api/client";
import { useApiQuery } from "../app/Page";
import { useBff } from "../app/session";
import { useT } from "../i18n";
import { Button } from "../ui/Button";
import { ErrorState } from "../ui/states";
import { CopilotError } from "./copilot/Copilot";

type Draft = Schemas["DraftOut"];
type Spec = {
  instructions?: string; greeting?: { text?: string }; tools?: { builtin?: string; name?: string }[];
  flow?: { nodes?: Record<string, unknown> }; post_call?: { summary?: boolean; extraction?: unknown; qa?: { items?: unknown[] } };
};
const NAME = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const field = "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm";

// the languages the dialog offers; the description's script picks one until the user does (V1.2-UX-T04)
const LANGUAGES = ["en-IN", "en-US", "en-GB", "hi-IN", "bn-IN", "ta-IN", "te-IN", "kn-IN", "ml-IN", "mr-IN", "gu-IN",
  "pa-IN", "od-IN", "es-ES", "fr-FR", "de-DE"] as const;
const SCRIPTS: [RegExp, string][] = [
  [/[ऀ-ॿ]/, "hi-IN"], [/[ঀ-৿]/, "bn-IN"], [/[਀-੿]/, "pa-IN"], [/[઀-૿]/, "gu-IN"],
  [/[଀-୿]/, "od-IN"], [/[஀-௿]/, "ta-IN"], [/[ఀ-౿]/, "te-IN"], [/[ಀ-೿]/, "kn-IN"],
  [/[ഀ-ൿ]/, "ml-IN"],
];
const SLOW_AFTER_S = 20;

/** The language a description is written in, by its script; Latin script is English (India's for an Indian tenant). */
export function guessLanguage(text: string, country?: string | null): string {
  return SCRIPTS.find(([script]) => script.test(text))?.[1] ?? (country === "IN" ? "en-IN" : "en-US");
}

export function DescribeAgent({ onDone }: { onDone: () => void }) {
  const t = useT();
  const { api } = useBff();
  const ids = { use: useId(), what: useId(), language: useId(), base: useId(), name: useId() };
  const tenant = useApiQuery("tenant", (a) => a.GET("/v1/tenant"));
  const templates = useApiQuery("agent-templates:any", (a) => a.GET("/v1/agent-templates"));
  const agents = useApiQuery("agents", (a) => a.GET("/v1/agents", { params: { query: { limit: 100 } } }));
  const [direction, setDirection] = useState<"inbound" | "outbound">("inbound");
  const [useCase, setUseCase] = useState("");
  const [description, setDescription] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const language = picked ?? guessLanguage(description, tenant.data?.country);
  const [base, setBase] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [started, setStarted] = useState(0);
  const [now, setNow] = useState(0);
  const elapsed = Math.max(0, Math.floor((now - started) / 1000));
  const cancel = useRef<AbortController | null>(null);
  const make = useMutation({
    mutationFn: () => {
      cancel.current = new AbortController();
      const [kind, id] = base.split(":") as ["template" | "agent" | "", string];
      return data(api.POST("/v1/copilot/drafts", {
        body: {
          description: description.trim(), direction, language,
          ...(useCase.trim() ? { use_case: useCase.trim() } : {}),
          ...(kind === "agent" ? { base: { agent: id } } : kind === "template" ? { base: { template: id } } : {}),
        },
        signal: cancel.current.signal,
      }));
    },
    onMutate: () => {
      const at = Date.now();
      setStarted(at);
      setNow(at);
    },
    onSuccess: setDraft,
  });
  useEffect(() => {
    if (!make.isPending) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [make.isPending]);
  const stop = () => {
    cancel.current?.abort();
    make.reset();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (description.trim().length >= 3) make.mutate();
  };
  return (
    <div className="space-y-5">
      <form onSubmit={submit} className="space-y-3">
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium">{t("describe.direction")}</legend>
          <div className="flex flex-wrap gap-4 text-sm">
            {(["inbound", "outbound"] as const).map((d) => (
              <label key={d} className="flex items-center gap-2">
                <input type="radio" name="direction" value={d} checked={direction === d} onChange={() => setDirection(d)}
                  className="size-4 accent-[var(--color-primary)]" />
                {t(`describe.${d}`)}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="space-y-1">
          <label htmlFor={ids.use} className="block text-sm font-medium">{t("describe.useCase")}</label>
          <input id={ids.use} value={useCase} onChange={(e) => setUseCase(e.target.value)} maxLength={120}
            placeholder={t("describe.useCasePlaceholder")} className={field} />
        </div>
        <div className="space-y-1">
          <label htmlFor={ids.what} className="block text-sm font-medium">{t("describe.what")}</label>
          <textarea id={ids.what} rows={5} value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder={t("describe.placeholder")} className={field} aria-describedby={`${ids.what}-hint`} />
          <p id={`${ids.what}-hint`} className="text-xs text-[var(--color-muted)]">{t("describe.hint")}</p>
        </div>
        <div className="space-y-1">
          <label htmlFor={ids.language} className="block text-sm font-medium">{t("describe.language")}</label>
          <select id={ids.language} value={language} onChange={(e) => setPicked(e.target.value)} className={field}
            aria-describedby={`${ids.language}-hint`}>
            {LANGUAGES.map((l) => <option key={l} value={l}>{t(`describe.lang.${l}`)}</option>)}
          </select>
          <p id={`${ids.language}-hint`} className="text-xs text-[var(--color-muted)]">{t("describe.languageHint")}</p>
        </div>
        <details className="rounded-md border border-[var(--color-border)] p-3 text-sm">
          <summary className="cursor-pointer font-medium">{t("describe.more")}</summary>
          <div className="mt-3 space-y-1">
            <label htmlFor={ids.base} className="block text-sm font-medium">{t("describe.base")}</label>
            <select id={ids.base} value={base} onChange={(e) => setBase(e.target.value)} className={field}
              aria-describedby={`${ids.base}-hint`}>
              <option value="">{t("describe.baseAuto")}</option>
              <optgroup label={t("describe.templates")}>
                {(templates.data?.data ?? []).map((tp) => <option key={tp.id} value={`template:${tp.id}`}>{tp.title}</option>)}
              </optgroup>
              {(agents.data?.data ?? []).length > 0 && (
                <optgroup label={t("describe.agents")}>
                  {(agents.data?.data ?? []).map((a) => <option key={a.id} value={`agent:${a.id}`}>{a.name}</option>)}
                </optgroup>
              )}
            </select>
            <p id={`${ids.base}-hint`} className="text-xs text-[var(--color-muted)]">{t("describe.baseHint")}</p>
          </div>
        </details>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={description.trim().length < 3 || make.isPending}>
            {make.isPending ? <Loader2 size={16} aria-hidden="true" className="animate-spin" /> : <Sparkles size={16} aria-hidden="true" />}
            {make.isPending ? t("describe.draftingFor", { seconds: elapsed }) : t(draft ? "describe.again" : "describe.draft")}
          </Button>
          {make.isPending && <Button variant="secondary" onClick={stop}>{t("describe.cancel")}</Button>}
        </div>
        {make.isPending && elapsed >= SLOW_AFTER_S && (
          <p role="status" className="text-xs text-[var(--color-muted)]">{t("describe.slow")}</p>
        )}
        {make.isError && <DraftError error={make.error} onRetry={() => make.mutate()} retrying={make.isPending} />}
      </form>
      {draft && <DraftPreview key={JSON.stringify(draft.document.metadata)} draft={draft} onDone={onDone} nameId={ids.name} />}
    </div>
  );
}

function DraftPreview({ draft, onDone, nameId }: { draft: Draft; onDone: () => void; nameId: string }) {
  const t = useT();
  const { api } = useBff();
  const navigate = useNavigate();
  const queries = useQueryClient();
  const meta = draft.document.metadata as { name: string; description?: string };
  const kits = useApiQuery("agent-templates:any", (a) => a.GET("/v1/agent-templates"));
  const kitLabel = kits.data?.kits?.find((k) => k.id === draft.kit)?.label ?? draft.kit ?? "";
  const spec = draft.document.spec as Spec;
  const [name, setName] = useState(meta.name);
  const create = useMutation({
    mutationFn: () => data(api.POST("/v1/agents", {
      body: { ...(draft.document as Schemas["AgentDocument"]), metadata: { ...meta, name } },
    })),
    onSuccess: async (agent) => {
      await queries.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("agents") });
      onDone();
      await navigate({ to: "/agents/$agentId", params: { agentId: agent.id }, search: { created: true } });
    },
  });
  const nameProblem = name && !NAME.test(name) ? t("agents.new.nameRule") : null;
  const conflict = create.error instanceof ApiError && create.error.status === 409;
  const tools = (spec.tools ?? []).map((tool) => tool.builtin ?? tool.name ?? "");
  const steps = Object.keys(spec.flow?.nodes ?? {});
  const warnings = draft.notices.filter((n) => n.severity === "warning");
  const infos = draft.notices.filter((n) => n.severity === "info");
  return (
    <section aria-label={t("describe.preview")} className="space-y-3 rounded-lg border border-[var(--color-border)] p-4">
      <h3 className="font-semibold">{t("describe.preview")}</h3>
      {draft.kit && <p className="text-sm text-[var(--color-muted)]">{t("describe.kitUsed", { kit: kitLabel })}</p>}
      {draft.fallback && <p role="status" className="text-sm text-[var(--color-muted)]">{t("describe.fallbackUsed")}</p>}
      {meta.description && <p className="text-sm">{meta.description}</p>}
      <dl className="space-y-2 text-sm">
        {spec.greeting?.text && <Fact label={t("describe.greeting")}>{spec.greeting.text}</Fact>}
        <Fact label={t("describe.instructions")}><span className="whitespace-pre-wrap">{spec.instructions}</span></Fact>
        <Fact label={t("describe.tools")}>{tools.length ? tools.join(", ") : t("describe.none")}</Fact>
        {steps.length > 0 && <Fact label={t("describe.steps")}>{steps.join(" → ")}</Fact>}
        <Fact label={t("describe.afterCall")}>
          {[spec.post_call?.summary && t("describe.summary"), spec.post_call?.extraction ? t("describe.extraction") : null,
            spec.post_call?.qa ? t("describe.qa", { count: spec.post_call.qa.items?.length ?? 0 }) : null]
            .filter(Boolean).join(", ") || t("describe.none")}
        </Fact>
      </dl>
      {warnings.length > 0 && (
        <ul aria-label={t("describe.warnings")} className="space-y-1 text-sm text-[var(--color-warning)]">
          {warnings.map((w, i) => <li key={i}><code className="text-xs">{w.path}</code> {w.message}</li>)}
        </ul>
      )}
      {infos.length > 0 && (
        <ul className="space-y-1 text-sm text-[var(--color-muted)]">{infos.map((n, i) => <li key={i}>{n.message}</li>)}</ul>
      )}
      <p className="text-xs text-[var(--color-muted)]">{t("describe.reviewNote")}</p>
      <div className="space-y-1">
        <label htmlFor={nameId} className="block text-sm font-medium">{t("agents.new.name")}</label>
        <input id={nameId} value={name} onChange={(e) => { setName(e.target.value.toLowerCase()); create.reset(); }}
          aria-invalid={Boolean(nameProblem || conflict)} aria-describedby={`${nameId}-hint`}
          className={`${field} font-mono`} autoComplete="off" spellCheck={false} />
        <p id={`${nameId}-hint`} className={`text-sm ${nameProblem || conflict ? "text-[var(--color-danger)]" : "text-[var(--color-muted)]"}`}>
          {conflict ? t("agents.new.nameTaken", { name }) : (nameProblem ?? t("agents.new.nameHint"))}
        </p>
      </div>
      {create.isError && !conflict && <ErrorState error={create.error} />}
      <Button onClick={() => create.mutate()} disabled={!name || Boolean(nameProblem) || create.isPending}>
        {t(create.isPending ? "agents.new.creating" : "agents.new.create")}
      </Button>
    </section>
  );
}

/**
 * Why there is no draft, by kind (V1.2-UX-T01): the model's provider is unavailable, the draft ran out of time, or the
 * model's answers made no valid agent. Each can be tried again; anything else is the copilot's usual error.
 */
export function DraftError({ error, onRetry, retrying }: { error: unknown; onRetry: () => void; retrying: boolean }) {
  const t = useT();
  const problem = error instanceof ApiError ? error.problem : null;
  const kind = problem?.code === "unavailable" ? "unavailable"
    : problem?.code === "upstream_timeout" ? "timeout"
    : problem?.code === "upstream_error" && (problem.errors?.length ?? 0) > 0 ? "invalid" : null;
  if (!kind) return <CopilotError error={error} />;
  return (
    <div role="alert" className="space-y-2 rounded-md border border-[var(--color-danger)] p-3 text-sm">
      <p className="font-medium">{t(`describe.error.${kind}`)}</p>
      {problem?.detail && <p className="text-[var(--color-muted)]">{problem.detail}</p>}
      {kind === "invalid" && (
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-[var(--color-muted)]">
          {(problem?.errors ?? []).slice(0, 3).map((e, i) => <li key={i}><code>{e.field || "(answer)"}</code> {e.message}</li>)}
        </ul>
      )}
      <Button variant="secondary" onClick={onRetry} disabled={retrying}>{t("describe.tryAgain")}</Button>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-[var(--color-muted)]">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
